import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import DateBox from "@/components/ui/DateBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import SelectBox from "@/components/ui/SelectBox";
import CountBox from "@/components/ui/CountBox";
import { useMemo, useState, useEffect } from "react";
import { useInfiniteScrollPage } from "@/hooks/components/useInfiniteScrollPage";
import { Footer } from "@/components/layout/Footer";
import GroupTable from "@/components/ui/GroupTable";
import { TableColumn } from "@/components/ui/Table";
import { formatNumber, sumBy } from "@/lib/utils";
import { useSalesByClient } from "@/hooks/settlement/se_salesByClient/useSalesByClient";
import { SalesByClient } from "@/interface/settlement/salesByClient";
import { useCodeListByCompanyCode } from "@/hooks/standard/st_code/useCode";
import { Code } from "@/interface/standard/st_code/index";
import ExcelButton from "@/components/ui/ExcelButton";
import PrintButton from "@/components/ui/PrintButton";
import { getToday } from "@/lib/date";

export default function SalesByClientPage() {
  const today = getToday();

  const [company, setCompany] = useState("");
  const [searchType, setSearchType] = useState("companyName");
  const [searchValue, setSearchValue] = useState("");
  const [itemSearchType, setItemSearchType] = useState("itemName");
  const [itemSearchValue, setItemSearchValue] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [dateBtnValue, setDateBtnValue] = useState("today");
  const [exactMatch, setExactMatch] = useState(false);

  // 실제 검색에 사용되는 값 (초기값으로 오늘 날짜 설정)
  const [actualParams, setActualParams] = useState({
    startDate: today,
    endDate: today,
    company: "",
    searchType: "companyName",
    search: "",
    exactMatch: false,
    itemSearchType: "itemName",
    itemSearch: "",
  });

  // 회사 목록
  const { data: companyCodeList = [] } = useCodeListByCompanyCode();

  // API 호출
  const { data: salesData = [], isLoading } = useSalesByClient(actualParams);

  // 날짜 변경 시 자동 조회
  useEffect(() => {
    setActualParams(prev => ({
      ...prev,
      startDate,
      endDate,
    }));
  }, [startDate, endDate]);

  // 무한 스크롤 페이지 훅
  const infiniteScrollPage = useInfiniteScrollPage({
    data: salesData,
    initialLoadCount: 30,
    loadMoreCount: 30,
    onDataLoad: async (_current, _total) => {
      // 필요 시 추가 로드
    },
  });

  // 회사 옵션
  const companyOptions = useMemo(
    () => [
      { label: "전체", value: "" },
      ...companyCodeList.map((item: Code) => ({
        value: item.code || item.codeId?.toString() || "",
        label: item.name || "",
      })),
    ],
    [companyCodeList]
  );

  const searchTypeOptions = [
    { label: "매출처", value: "companyName" },
    { label: "업체코드", value: "companyCode" },
  ];

  const itemSearchTypeOptions = [
    { label: "품목명", value: "itemName" },
    { label: "품목코드", value: "itemCode" },
  ];

  const handleReset = () => {
    const resetDate = getToday();
    setCompany("");
    setSearchType("companyName");
    setSearchValue("");
    setItemSearchType("itemName");
    setItemSearchValue("");
    setStartDate(resetDate);
    setEndDate(resetDate);
    setDateBtnValue("today");
    setExactMatch(false);
    setActualParams({
      startDate: resetDate,
      endDate: resetDate,
      company: "",
      searchType: "companyName",
      search: "",
      exactMatch: false,
      itemSearchType: "itemName",
      itemSearch: "",
    });
  };

  const handleSearch = () => {
    setActualParams({
      startDate,
      endDate,
      company,
      searchType,
      search: searchValue,
      exactMatch,
      itemSearchType,
      itemSearch: itemSearchValue,
    });
  };

  // 테이블 컬럼
  const columns: TableColumn[] = [
    { key: "companyCode", header: "업체코드", bodyAlign: "center", width: "100px" },
    { key: "companyName", header: "매출처", bodyAlign: "left", width: "180px" },
    { key: "itemCode", header: "품목코드", bodyAlign: "center", width: "100px" },
    { key: "itemName", header: "품목명", bodyAlign: "left", width: "150px" },
    { key: "qty", header: "매출수량", bodyAlign: "right", width: "100px", render: (v: number) => (v ?? 0).toFixed(2) },
    { key: "salesUnit", header: "운송단가", bodyAlign: "right", width: "100px", group: "매출액", render: (v: number) => formatNumber(v ?? 0) },
    { key: "salesAmt", header: "금액", bodyAlign: "right", width: "120px", group: "매출액", render: (v: number) => formatNumber(v ?? 0) },
    { key: "purchaseUnit", header: "운송단가", bodyAlign: "right", width: "100px", group: "매입액", render: (v: number) => formatNumber(v ?? 0) },
    { key: "purchaseAmt", header: "금액", bodyAlign: "right", width: "120px", group: "매입액", render: (v: number) => formatNumber(v ?? 0) },
    { key: "marginAmt", header: "금액", bodyAlign: "right", width: "120px", group: "운송마진", render: (v: number) => formatNumber(v ?? 0) },
    { key: "marginRate", header: "비율", bodyAlign: "right", width: "80px", group: "운송마진", render: (v: number) => `${(v ?? 0).toFixed(2)}%` },
  ];

  // Footer
  const footerGrid = useMemo(() => {
    const totalQty = sumBy(salesData, "qty");
    const totalSales = sumBy(salesData, "salesAmt");
    const totalPurchase = sumBy(salesData, "purchaseAmt");
    const totalMargin = sumBy(salesData, "marginAmt");
    const marginRate = totalSales > 0 ? (totalMargin / totalSales) * 100 : 0;

    return [[
      { label: "매출수량", value: totalQty.toFixed(2) },
      { label: "매출액", value: formatNumber(totalSales) },
      { label: "매입액", value: formatNumber(totalPurchase) },
      { label: "운송마진", value: formatNumber(totalMargin) },
      { label: "마진비율", value: `${marginRate.toFixed(2)}%` },
    ]];
  }, [salesData]);

  return (
    <div className="flex flex-col h-full">
      {/* 검색영역 */}
      <SearchBoxLayout>
        <div className="flex items-end">
          <SelectBox
            label="회사"
            className="w-40"
            value={company}
            onChange={setCompany}
            options={companyOptions}
          />
          <div className="mr-3" />
          <SelectBox
            label="구분"
            className="w-28"
            value={searchType}
            onChange={setSearchType}
            options={searchTypeOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-40"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            placeholder="입력"
            onKeyDown={e => { if (e.key === "Enter") handleSearch(); }}
          />
          <div className="mr-3" />
          <SelectBox
            label="구분"
            className="w-28"
            value={itemSearchType}
            onChange={setItemSearchType}
            options={itemSearchTypeOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-40"
            value={itemSearchValue}
            onChange={e => setItemSearchValue(e.target.value)}
            placeholder="입력"
            onKeyDown={e => { if (e.key === "Enter") handleSearch(); }}
          />
          <div className="mr-3" />

          <DateBox
            label="매출일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-2" />
          <DateSearchBtn
            mode="range"
            value={dateBtnValue}
            onChangeValue={setDateBtnValue}
            currentRange={{ startDate, endDate }}
            onDateSelect={({ startDate, endDate }) => { setStartDate(startDate); setEndDate(endDate); }}
          />

          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>초기화</Button>
            <Button variant="search" onClick={handleSearch}>조회</Button>
          </div>
        </div>
      </SearchBoxLayout>

      {/* 상단 버튼/카운트 */}
      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={salesData.length} />
          <div className="flex gap-2">
            <PrintButton
              data={salesData}
              columns={columns}
              fileName="거래처별매출액명세서"
              title="거래처별 매출액 명세서"
              skipPermissionCheck
            />
            <ExcelButton
              data={salesData}
              columns={columns}
              fileName="거래처별매출액명세서"
              skipPermissionCheck
            />
          </div>
        </div>

        {/* 본문 표 */}
        <div className="flex-1 min-h-0">
          <GroupTable
            key={infiniteScrollPage.tableKey}
            className="h-full"
            resizable
            {...infiniteScrollPage.tableProps}
            columns={columns}
            loading={isLoading}
            emptyMessage="조회 조건을 입력하고 조회 버튼을 클릭하세요"
            // 그룹 설정
            groupBy={(r: SalesByClient) => `${r.companyCode} ${r.companyName}`}
            sumColumns={["qty", "salesUnit", "salesAmt", "purchaseUnit", "purchaseAmt", "marginAmt"]}
            summaryLabelColumn="itemName"
            getSummaryLabel={(k) => `${k} 합계`}
            // 합계행 스타일
            summaryRowBg="#EAF3D9"
            summaryRowText="#2D4A1A"
            summaryRowBorderLeft="#B7D58A"
            defaultCollapsed={false}
            maxHeight="100%"
          />
        </div>
      </main>

      {/* 푸터 총계 */}
      <footer className="flex-shrink-0 p-4 bg-gray-50 border-t">
        <Footer grid={footerGrid} />
      </footer>
    </div>
  );
}
